import React, { useState } from "react";
import { VendorShipmentDetails, VendorAddress } from "src/types/database/vendor";
import { ValidationErrors, ValidationError } from "src/errors";
import { CollapsibleSection } from "src/components/ui/collapsible";
import BusinessInfoSection from "./BusinessInfoSection";
import AddressesSection from "./AddressesSection";
import ContactInfoSection from "./ContactInfoSection";
import { CreateVendorFormDataKey, UpdateVendorFormDataKey } from "src/types";
import { CreateVendorFormDataValidator, UpdateVendorFormDataValidator } from "src/validation/vendor";
import "src/tailwind.css";

interface ShipmentDetailsFormProps {
  shipmentDetails: VendorShipmentDetails;
  validationErrors?: ValidationErrors<CreateVendorFormDataKey | UpdateVendorFormDataKey>;
  onFieldChange: (field: keyof VendorShipmentDetails, value: any) => void;
  isUpdateMode?: boolean;
}

const ShipmentDetailsForm: React.FC<ShipmentDetailsFormProps> = ({
  shipmentDetails,
  validationErrors,
  onFieldChange,
  isUpdateMode = false
}) => {
  const [fieldErrors, setFieldErrors] = useState<Map<string, string>>(new Map());
  const [sectionStates, setSectionStates] = useState({
    businessInfo: true,
    addresses: true,
    contactInfo: true
  });
  const [addressStates, setAddressStates] = useState<Map<number, boolean>>(new Map());

  // Validate individual fields and update field-specific errors
  const validateField = (fieldName: string, value: any) => {
    const validator = isUpdateMode ? UpdateVendorFormDataValidator : CreateVendorFormDataValidator;
    let error: ValidationError<string> | undefined;

    if (fieldName.startsWith("address_")) {
      // Handle address field validation
      const addressIndex = parseInt(fieldName.split("_")[1]);
      const addressField = fieldName.split("_").slice(2).join("_");
      const address = shipmentDetails.addresses?.[addressIndex];

      if (address) {
        switch (addressField) {
          case "line1":
            if (!value?.trim()) {
              error = new ValidationError("Address line 1 is required", fieldName);
            } else if (value.length > 200) {
              error = new ValidationError("Address line 1 must be 200 characters or less", fieldName);
            }
            break;
          case "line2":
            if (value && value.length > 200) {
              error = new ValidationError("Address line 2 must be 200 characters or less", fieldName);
            }
            break;
          case "line3":
            if (value && value.length > 200) {
              error = new ValidationError("Address line 3 must be 200 characters or less", fieldName);
            }
            break;
          case "city":
            if (!value?.trim()) {
              error = new ValidationError("City is required", fieldName);
            }
            break;
          case "country":
            if (!value?.trim()) {
              error = new ValidationError("Country is required", fieldName);
            }
            break;
          case "zipCode":
            if (!value?.trim()) {
              error = new ValidationError("Zip code is required", fieldName);
            }
            break;
          case "countryCode":
            if (!value?.trim()) {
              error = new ValidationError("Country code is required", fieldName);
            } else if (value.length !== 2) {
              error = new ValidationError("Country code must be exactly 2 characters", fieldName);
            }
            break;
        }
      }
    } else {
      error = validator.validateShipmentDetailField(fieldName as keyof VendorShipmentDetails, value);
    }

    setFieldErrors((prev) => {
      const newErrors = new Map(prev);
      if (error) {
        newErrors.set(fieldName, error.message);
      } else {
        newErrors.delete(fieldName);
      }
      return newErrors;
    });
  };

  // Handle field changes with validation
  const handleFieldChange = (field: keyof VendorShipmentDetails, value: any) => {
    onFieldChange(field, value);
    validateField(field, value);
  };

  // Handle address field changes
  const handleAddressChange = (addressIndex: number, field: keyof VendorAddress, value: any) => {
    const currentAddresses = shipmentDetails.addresses || [];
    const updatedAddresses = [...currentAddresses];

    if (!updatedAddresses[addressIndex]) {
      updatedAddresses[addressIndex] = {
        line1: "",
        city: "",
        zipCode: "",
        countryCode: "",
        isCurrent: false
      };
    }

    updatedAddresses[addressIndex] = {
      ...updatedAddresses[addressIndex],
      [field]: value
    };

    onFieldChange("addresses", updatedAddresses);
    validateField(`address_${addressIndex}_${field}`, value);
  };

  // Add new address
  const addAddress = () => {
    const currentAddresses = shipmentDetails.addresses || [];
    const newAddress: VendorAddress = {
      line1: "",
      city: "",
      zipCode: "",
      countryCode: "",
      isCurrent: currentAddresses.length === 0 // First address is current by default
    };

    onFieldChange("addresses", [...currentAddresses, newAddress]);
  };

  // Remove address
  const removeAddress = (index: number) => {
    const currentAddresses = shipmentDetails.addresses || [];
    const updatedAddresses = currentAddresses.filter((_, i) => i !== index);

    // If we removed the current address, make the first remaining address current
    if (currentAddresses[index]?.isCurrent && updatedAddresses.length > 0) {
      updatedAddresses[0].isCurrent = true;
    }

    // Clean up address states - shift down indices after the removed one
    setAddressStates((prev) => {
      const newMap = new Map();
      prev.forEach((value, key) => {
        if (key < index) {
          newMap.set(key, value);
        } else if (key > index) {
          newMap.set(key - 1, value);
        }
        // Skip the removed index
      });
      return newMap;
    });

    onFieldChange("addresses", updatedAddresses);
  };

  // Set current address
  const setCurrentAddress = (index: number) => {
    const currentAddresses = shipmentDetails.addresses || [];
    const updatedAddresses = currentAddresses.map((addr, i) => ({
      ...addr,
      isCurrent: i === index
    }));

    onFieldChange("addresses", updatedAddresses);
  };

  // Get error message for a specific field
  const getFieldErrorMessage = (fieldName: string): string | undefined => {
    return fieldErrors.get(fieldName);
  };

  // Handle section toggle
  const handleSectionToggle = (section: keyof typeof sectionStates, isOpen: boolean) => {
    setSectionStates((prev) => ({
      ...prev,
      [section]: isOpen
    }));
  };

  // Handle address section toggle
  const handleAddressToggle = (index: number, isOpen: boolean) => {
    setAddressStates((prev) => {
      const newMap = new Map(prev);
      newMap.set(index, isOpen);
      return newMap;
    });
  };

  // Get address section state
  const getAddressState = (index: number): boolean => {
    return addressStates.get(index) ?? false;
  };

  return (
    <div className="space-y-6 p-6 bg-secondary-50 min-h-screen">
      <CollapsibleSection
        title="📋 Business Information"
        isOpen={sectionStates.businessInfo}
        onToggle={(isOpen) => handleSectionToggle("businessInfo", isOpen)}
        variant="elevated"
      >
        <BusinessInfoSection
          shipmentDetails={shipmentDetails}
          onFieldChange={handleFieldChange}
          getFieldErrorMessage={getFieldErrorMessage}
        />
      </CollapsibleSection>

      <AddressesSection
        shipmentDetails={shipmentDetails}
        getFieldErrorMessage={getFieldErrorMessage}
        onAddressChange={handleAddressChange}
        addAddress={addAddress}
        removeAddress={removeAddress}
        setCurrentAddress={setCurrentAddress}
        getAddressState={getAddressState}
        onAddressToggle={handleAddressToggle}
        isOpen={sectionStates.addresses}
        onToggle={(isOpen) => handleSectionToggle("addresses", isOpen)}
      />

      <CollapsibleSection
        title="📞 Contact Information"
        isOpen={sectionStates.contactInfo}
        onToggle={(isOpen) => handleSectionToggle("contactInfo", isOpen)}
        variant="elevated"
      >
        <ContactInfoSection
          shipmentDetails={shipmentDetails}
          onFieldChange={handleFieldChange}
          getFieldErrorMessage={getFieldErrorMessage}
        />
      </CollapsibleSection>
    </div>
  );
};

export { ShipmentDetailsForm };
